import React from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine, LabelList } from 'recharts';
interface LineChartVisualProps {
  data: Array<{
    label: string;
    value: number;
  }>;
  xAxisLabel?: string;
  yAxisLabel?: string;
  lineColor?: string;
  hideValues?: boolean;
  valueDisplay?: 'tooltip' | 'on-line' | 'on-axis-line';
  mode?: 'stimulus' | 'support';
}
export function LineChartVisual({
  data,
  xAxisLabel = '',
  yAxisLabel = '',
  lineColor = '#6366f1',
  hideValues = true,
  valueDisplay,
  mode = 'stimulus'
}: LineChartVisualProps) {
  const effectiveHideValues = mode === 'support' ? false : hideValues;
  const effectiveValueDisplay = valueDisplay || (effectiveHideValues ? 'on-axis-line' : 'on-line');
  const CustomTooltip = ({
    active,
    payload
  }: {
    active?: boolean;
    payload?: Array<{
      payload: {
        label: string;
        value: number;
      };
    }>;
  }) => {
    if (!active || !payload?.[0]) return null;
    const item = payload[0].payload;
    return <div className="bg-white px-3 py-2 rounded-lg shadow-lg border border-slate-200 text-sm" data-id="element-13619" data-mp="3j9cpz04lcgyf">
        <span className="font-semibold text-slate-700" data-id="element-13620" data-mp="3f5emyec5am03">{item.label}</span>
        {!effectiveHideValues && <span className="text-slate-500 ml-2" data-id="element-13621" data-mp="3t4o35e0cpg42">{item.value}</span>}
      </div>;
  };
  const CrosshairCursor = (props: any) => {
    const {
      activeCoordinate,
      width,
      height,
      left,
      top
    } = props;
    if (!activeCoordinate) return null;
    const {
      x,
      y
    } = activeCoordinate;
    return <g data-id="element-13622" data-mp="3a6lbsskbz733">
        <line x1={x} y1={top} x2={x} y2={top + height} stroke="#94a3b8" strokeDasharray="4 4" data-id="element-13623" data-mp="3a6bml77413yg" />
        
        <line x1={left} y1={y} x2={left + width} y2={y} stroke="#94a3b8" strokeDasharray="4 4" data-id="element-13624" data-mp="35fcznorm97m8" />
        
      </g>;
  };
  return <div className="w-full max-w-3xl mx-auto flex flex-col items-center" data-id="element-13625" data-mp="2hp90pupdhw1k">
      {/* Visually hidden table for screen readers */}
      <table className="sr-only" data-id="element-13626" data-mp="2cp3e4sxymzqc">
        <caption data-id="element-13627" data-mp="gob0rfdhm9o3">
          Lijngrafiek data
          {xAxisLabel || yAxisLabel ? ` voor ${xAxisLabel} en ${yAxisLabel}` : ''}
        </caption>
        <thead data-id="element-13628" data-mp="llkzxmqohzp4">
          <tr data-id="element-13629" data-mp="ll1hm4x499ak">
            <th scope="col" data-id="element-13630" data-mp="lkt7gksdoi32">{xAxisLabel || 'Categorie'}</th>
            <th scope="col" data-id="element-13631" data-mp="qf5sv0df03hg">{yAxisLabel || 'Waarde'}</th>
          </tr>
        </thead>
        <tbody data-id="element-13632" data-mp="ueejvxh2ln9b">
          {data.map((item, i) => <tr key={i} data-id="element-13633" data-mp="uhk694ybeb1c" data-mpi={i}>
              <td data-id="element-13634" data-mp="3tuuq5hfge64l">{item.label}</td>
              <td data-id="element-13635" data-mp="3tvwcwj5u0h25">{item.value}</td>
            </tr>)}
        </tbody>
      </table>

      <div className="w-full h-[240px] md:h-[340px] bg-white rounded-xl border border-slate-200 shadow-sm p-3 md:p-4" data-id="element-13636" data-mp="2ni68un6rekz">
        <ResponsiveContainer width="100%" height="100%" data-id="element-13637" data-mp="6p3rb39viz69">
          <LineChart data={data} margin={{
          top: 20,
          right: 30,
          left: 20,
          bottom: 40
        }} data-id="element-13638" data-mp="boq45blsidej">
            
            <CartesianGrid strokeDasharray="3 3" vertical={false} data-id="element-13639" data-mp="1i4iljzap7cxo" />
            <XAxis dataKey="label" interval={0} tick={{
            fill: '#64748b',
            fontSize: 12
          }} tickLine={false} axisLine={{
            stroke: '#cbd5e1'
          }} label={xAxisLabel ? {
            value: xAxisLabel,
            position: 'insideBottom',
            offset: -20,
            fill: '#475569',
            fontSize: 13,
            fontWeight: 600
          } : undefined} data-id="element-13640" data-mp="1n3c1rzhddi1j" />
            
            <YAxis tick={{
            fill: '#64748b',
            fontSize: 12
          }} tickLine={false} axisLine={false} label={yAxisLabel ? {
            value: yAxisLabel,
            angle: -90,
            position: 'insideLeft',
            fill: '#475569',
            fontSize: 14,
            fontWeight: 600
          } : undefined} data-id="element-13641" data-mp="1jg5gofpyp2g8" />
            
            <Tooltip content={<CustomTooltip data-id="element-13643" data-mp="10iuhsw88sb1b" />} cursor={<CrosshairCursor data-id="element-13644" data-mp="10jm41gfy8wex" />} data-id="element-13642" data-mp="10ikslav0u7wo" />

            {effectiveValueDisplay === 'on-axis-line' && Array.from(new Set(data.map(d => d.value))).map((val, i) => <ReferenceLine key={`ref-${i}`} y={val} stroke="#94a3b8" strokeDasharray="3 3" opacity={0.5} data-id="element-13645" data-mp="2uw3okg9elifv" data-mpi={i} />)}

            <Line type="monotone" dataKey="value" stroke={lineColor} strokeWidth={3} dot={{
            fill: '#fff',
            stroke: lineColor,
            strokeWidth: 2,
            r: 5
          }} activeDot={{
            fill: '#4f46e5',
            stroke: '#312e81',
            strokeWidth: 2,
            r: 7
          }} data-id="element-13646" data-mp="31yih8atncv5v">
              
              {effectiveValueDisplay === 'on-line' && <LabelList dataKey="value" position="top" fill="#475569" fontSize={12} fontWeight={600} offset={10} data-id="element-13647" data-mp="2j80ssifuklqb" />}
            </Line>
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>;
}